// Linkup API integration for Vite React (using import.meta.env)
// Usage: import linkupService from './linkupService';
//
// FILTERING SUMBER REFERENSI:
// - Ketika referenceSource = "Indonesia": Prioritas sumber .id, .co.id, dan media Indonesia
// - Ketika referenceSource = "International": Menghindari semua sumber Indonesia (.id, .co.id, media Indonesia)
// - Filtering dilakukan dalam 2 tahap: query modification dan post-processing filtering

// Get API key and URL from Vite environment variables
const LINKUP_API_KEY = import.meta.env.VITE_LINKUP_API_KEY || import.meta.env.LINKUP_API_KEY;
const LINKUP_API_URL = import.meta.env.VITE_LINKUP_API_URL || import.meta.env.LINKUP_API_URL || 'https://api.linkup.so/v1/search';

// Validate configuration
if (!LINKUP_API_KEY) {
  console.error('[Linkup] CRITICAL: LINKUP_API_KEY tidak ditemukan di environment variables!');
}

let requestCount = 0;
// Increased limit to accommodate normal usage before local throttling kicks in
const MAX_REQUESTS = 50;

/**
 * Service for interacting with the Linkup API
 * Provides search functionality with enhanced error handling, validation, and retry logic
 */
class LinkupService {
  /**
   * Initialize LinkupService with API key
   * @throws {Error} If VITE_LINKUP_API_KEY environment variable is not set
   */
  constructor() {
    if (!LINKUP_API_KEY) {
      throw new Error('VITE_LINKUP_API_KEY environment variable is required')
    }
    
    // Simple in-memory cache for search results
    this.cache = new Map()
    this.cacheTimeout = 5 * 60 * 1000 // 5 minutes
  }

  /**
   * Search with Linkup API
   * @param {string} query - Search query
   * @param {string} [searchType='web'] - Search type (web, academic) or depth (standard, deep)
   * @param {string} [outputType='searchResults'] - Output type
   * @param {string} [region='Indonesia'] - Region filter
   * @returns {Promise<{answer: string, sources: Array, images: Array}>}
   */
  async search(query, searchType = "web", outputType = "searchResults", region = "Indonesia") {
    // Map search types to API depth values
    const depthMapping = {
      'web': 'standard',
      'academic': 'standard',
      'standard': 'standard',
      'deep': 'deep'
    };
    
    const depth = depthMapping[searchType] || 'standard';
    const MAX_RETRIES = 2;
    const TIMEOUT_MS = 25000; // 25 detik
    let lastError = null;

    async function makeRequest(attempt) {
      try {
        if (!LINKUP_API_KEY) {
          console.error('[Linkup] LINKUP_API_KEY not found or invalid');
          return {
            answer: "API configuration invalid. Contact administrator.",
            sources: [],
            images: []
          };
        }
        if (requestCount >= MAX_REQUESTS) {
          console.log('[Linkup] Max request limit reached');
          return {
            answer: "API usage limit reached. Try again later.",
            sources: [],
            images: []
          };
        }
        requestCount++;
        console.log(`[Linkup] Request #${requestCount} of ${MAX_REQUESTS}`);

        // Setup filters berdasarkan region
        let filters = {};
        if (region === "Indonesia") {
          filters = {
            domain_filter: {
              include: [".id", ".co.id", "kompas.com", "detik.com", "tribunnews.com", "liputan6.com", "okezone.com", "merdeka.com", "suara.com", "cnnindonesia.com", "tempo.co", "republika.co.id", "kumparan.com", "jabarprov.go.id", "jakarta.go.id", "kemendagri.go.id", "dpr.go.id", "bps.go.id"],
              exclude: []
            }
          };
          console.log("[Linkup] Using Indonesia source filter");
        } else if (region === "International") {
          filters = {
            domain_filter: {
              exclude: [
                ".id", ".co.id", "kompas.com", "detik.com", "tribunnews.com", "liputan6.com", "okezone.com", "merdeka.com", "suara.com", "cnnindonesia.com", "tempo.co", "republika.co.id", "kumparan.com", "sindonews.com", "jawapos.com", "suaramerdeka.com", "pikiran-rakyat.com", "bisnis.com", "beritasatu.com", "cnbcindonesia.com", "go.id", "jabarprov.go.id", "jakarta.go.id", "kemendagri.go.id", "dpr.go.id", "bps.go.id", "depkes.go.id", "kemdikbud.go.id"
              ],
              include: [
                ".com", ".org", ".net", "bbc.com", "cnn.com", "nytimes.com", "theguardian.com", "reuters.com", "apnews.com", "washingtonpost.com", "economist.com", "forbes.com", "bloomberg.com"
              ]
            }
          };
          console.log("[Linkup] Using International source filter");
        }

        // Format request sesuai dokumentasi resmi Linkup API
        const requestOptions = {
          q: query,
          depth: depth,
          outputType: outputType,
          includeImages: true,
          maxResults: 50, // Increase max results to get more sources
          ...(Object.keys(filters).length > 0 && { filters })
        };

        // Use fetch with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

        console.log(`[Linkup] Sending request to ${LINKUP_API_URL} (attempt ${attempt + 1})`);
        console.log(`[Linkup] Query: "${query}", Region: "${region}"`);
        console.log(`[Linkup] Request options (sesuai dokumentasi):`, JSON.stringify(requestOptions, null, 2));
        console.log(`[Linkup] Headers:`, {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${LINKUP_API_KEY ? LINKUP_API_KEY.substring(0, 8) + '...' : 'MISSING'}`
        });
        
        const response = await fetch(LINKUP_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${LINKUP_API_KEY}`
          },
          body: JSON.stringify(requestOptions),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        console.log(`[Linkup] Response status: ${response.status}`);
        console.log(`[Linkup] Response headers:`, Object.fromEntries(response.headers.entries()));

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[Linkup] HTTP error response:`, errorText);
          throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
        }

        const responseText = await response.text();
        console.log(`[Linkup] Response text length: ${responseText.length}`);
        console.log(`[Linkup] Response text (first 1000 chars):`, responseText.substring(0, 1000));
        
        if (!responseText || responseText.trim() === '') {
          console.error('[Linkup] Empty response from API');
          return {
            answer: "Layanan pencarian referensi sedang tidak tersedia. Silakan coba beberapa saat lagi.",
            sources: [],
            images: []
          };
        }

        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('[Linkup] JSON parse error:', parseError);
          console.error('[Linkup] Raw response:', responseText);
          return {
            answer: "Format respons tidak valid dari layanan pencarian.",
            sources: [],
            images: []
          };
        }

        console.log('[Linkup] Parsed response data:', data);

        // Handle error responses
        if (data.error) {
          console.error('[Linkup] API returned error:', data.error);
          throw new Error(`API Error: ${data.error.message || data.error}`);
        }

        // Process and return the response
        return this.processResponse(data, query, region);

      } catch (error) {
        console.error(`[Linkup] Request attempt ${attempt + 1} failed:`, error);
        lastError = error;
        
        if (attempt < MAX_RETRIES - 1) {
          const delay = Math.pow(2, attempt) * 1000; // Exponential backoff
          console.log(`[Linkup] Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        throw error;
      }
    }

    // Try the request with retries
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        return await makeRequest.call(this, attempt);
      } catch (error) {
        if (attempt === MAX_RETRIES - 1) {
          // Last attempt failed
          console.error('[Linkup] All retry attempts failed:', lastError);
          return {
            answer: "Maaf, layanan pencarian sedang mengalami gangguan. Silakan coba lagi nanti.",
            sources: [],
            images: []
          };
        }
      }
    }
  }

  /**
   * Process and transform Linkup API response
   * @param {Object} response - Raw response from Linkup API
   * @param {string} query - Original search query
   * @param {string} [region='Indonesia'] - Region filter used
   * @returns {Object} Transformed response with sources, images, answer, and metrics
   * @private
   */
  processResponse(response, query, region = 'Indonesia') {
    console.log('[Linkup] Processing response:', response);
    
    // Extract data from response - Linkup API returns data in 'results' array
    const results = response.results || [];
    const answer = response.answer || response.text || '';
    
    console.log(`[Linkup] Extracted: answer length=${answer.length}, results=${results.length}`);
    
    // Separate text sources and images from results
    const textResults = results.filter(item => item.type === 'text');
    const imageResults = results.filter(item => item.type === 'image');
    
    console.log(`[Linkup] Text results: ${textResults.length}, Image results: ${imageResults.length}`);
    
    // Filter and validate text sources
    const validSources = textResults.filter(source => {
      if (!source) return false;
      
      // Check if source has required fields
      const hasTitle = source.name;
      const hasContent = source.content;
      const hasUrl = source.url;
      
      return hasTitle && hasContent && hasUrl;
    });
    
    console.log(`[Linkup] Valid sources after filtering: ${validSources.length}`);
    
    // Transform sources to match our app's expected format
    const transformedSources = validSources.map((source, index) => {
      const cleanUrl = source.url ? source.url.replace(/`/g, '').trim() : '#';
      let domain = 'unknown';
      
      try {
        if (cleanUrl && cleanUrl !== '#') {
          domain = new URL(cleanUrl).hostname;
        }
      } catch (e) {
        console.warn('[Linkup] Invalid URL for source:', cleanUrl);
        domain = 'unknown';
      }
      
      return {
        id: index + 1,
        title: source.name || 'Untitled Source',
        snippet: source.content ? source.content.substring(0, 200) + '...' : 'No description available',
        url: cleanUrl,
        domain: domain,
        isFallback: false
      };
    });
    
    // Transform images to match our app's expected format
    const transformedImages = imageResults.map((image, index) => {
      const cleanUrl = image.url ? image.url.replace(/`/g, '').trim() : '';
      let source = 'unknown';
      
      try {
        if (cleanUrl && cleanUrl !== '') {
          source = new URL(cleanUrl).hostname;
        }
      } catch (e) {
        console.warn('[Linkup] Invalid URL for image:', cleanUrl);
        source = 'unknown';
      }
      
      const transformedImage = {
        id: index + 1,
        url: cleanUrl,
        title: image.name || image.title || `Image ${index + 1}`,
        description: image.content || image.description || '',
        source: source
      };
      
      console.log(`[Linkup] Transformed image ${index + 1}:`, transformedImage);
      return transformedImage;
    }).filter(image => {
      const isValid = image.url && image.url !== '' && !image.url.includes('placeholder') && !image.url.includes('default');
      if (!isValid) {
        console.log('[Linkup] Filtered out invalid image:', image);
      }
      return isValid;
    });
    
    // Add fallback sources if no valid sources are available but we have an answer
    let finalSources = transformedSources;
    if (transformedSources.length === 0 && answer) {
      finalSources = [{
        id: 1,
        title: 'Linkup AI Response',
        snippet: 'Respons ini dihasilkan oleh AI Linkup berdasarkan analisis dari berbagai sumber web.',
        url: 'https://linkup.so',
        domain: 'linkup.so',
        isFallback: true
      }];
    }
    
    console.log(`[Linkup] Final sources count: ${finalSources.length}, Final images count: ${transformedImages.length}`);
    
    // Add citations to answer if sources are available
    const answerWithCitations = this.addCitationsToAnswer(answer, finalSources);
    
    // Transform response to match our app's expected format
    return {
      sources: finalSources,
      images: transformedImages,
      answer: answerWithCitations,
      searchType: region === 'Indonesia' ? 'web' : 'academic',
      rawSourcesCount: textResults.length,
      validSourcesCount: validSources.length,
      hasFallbackSources: finalSources.length > 0 && validSources.length === 0
    };
  }
  
  /**
   * Add citations to answer text based on available sources
   * @param {string} answer - Original answer text
   * @param {Array} sources - Available sources
   * @returns {string} Answer with citations added
   * @private
   */
  addCitationsToAnswer(answer, sources) {
    if (!answer || !sources || sources.length === 0) {
      return answer;
    }
    
    let answerWithCitations = answer;
    const sentences = answerWithCitations.split(/(?<=[.!?])\s+/);
    const usedSources = new Set();
    
    // Distribute citations more evenly across the content
    const targetCitationsPerSource = Math.max(1, Math.floor(sentences.length / sources.length));
    
    // For each source, try to find relevant content in the answer and add citations
    sources.forEach((source, index) => {
      const sourceIndex = index + 1;
      let citationsAdded = 0;
      
      // Extract key phrases from source title and snippet
      const sourceText = `${source.title} ${source.snippet}`.toLowerCase();
      const sourceWords = sourceText.split(/\s+/).filter(word => word.length > 3);
      
      // Find sentences in answer that might relate to this source
      sentences.forEach((sentence, sentenceIndex) => {
        if (citationsAdded >= targetCitationsPerSource) return;
        
        const sentenceLower = sentence.toLowerCase();
        
        // Count matching words between sentence and source
        const matchingWords = sourceWords.filter(word => 
          sentenceLower.includes(word) && 
          !['yang', 'dan', 'atau', 'dengan', 'untuk', 'dari', 'pada', 'dalam', 'adalah', 'ini', 'itu', 'akan', 'telah', 'sudah', 'dapat', 'bisa', 'juga', 'tidak', 'ada', 'the', 'and', 'or', 'with', 'for', 'from', 'in', 'is', 'this', 'that', 'will', 'has', 'been', 'can', 'could', 'also', 'not', 'have', 'about', 'would', 'should', 'could'].includes(word)
        );
        
        // Lower threshold for matching and allow multiple citations per sentence
        if (matchingWords.length >= 1 && !sentence.includes(`[${sourceIndex}]`) && !sentence.includes(`(${sourceIndex})`)) {
          // Check if sentence doesn't already have too many citations
          const existingCitations = (sentence.match(/\[\d+\]/g) || []).length;
          if (existingCitations < 3) { // Allow up to 3 citations per sentence
            // Find a good place to insert citation (before punctuation)
            const updatedSentence = sentence.replace(/([.!?])$/, ` [${sourceIndex}]$1`);
            answerWithCitations = answerWithCitations.replace(sentence, updatedSentence);
            citationsAdded++;
            usedSources.add(sourceIndex);
          }
        }
      });
    });
    
    // If some sources weren't used, add them to strategic locations
    sources.forEach((source, index) => {
      const sourceIndex = index + 1;
      if (!usedSources.has(sourceIndex) && sentences.length > 0) {
        // Find a sentence without citations or with fewer citations
        const sentenceToUpdate = sentences.find(sentence => {
          const citationCount = (sentence.match(/\[\d+\]/g) || []).length;
          return citationCount === 0 || citationCount < 2;
        });
        
        if (sentenceToUpdate && !sentenceToUpdate.includes(`[${sourceIndex}]`)) {
          const updatedSentence = sentenceToUpdate.replace(/([.!?])$/, ` [${sourceIndex}]$1`);
          answerWithCitations = answerWithCitations.replace(sentenceToUpdate, updatedSentence);
          usedSources.add(sourceIndex);
        }
      }
    });
    
    return answerWithCitations;
  }
  
  /**
    * Reset request count (useful for testing or manual reset)
    * @public
    */
   resetRequestCount() {
     requestCount = 0;
     console.log('[Linkup] Request count reset to 0');
   }
   
   /**
     * Get factual information with Indonesia sources priority
     * @param {string} query - Search query
     * @returns {Promise<{answer: string, sources: Array, images: Array}>}
     */
    async getFactualInfo(query) {
      return await this.search(query, "web", "searchResults", "Indonesia");
    }
    
    /**
     * Get scientific information with international sources priority
     * @param {string} query - Search query
     * @returns {Promise<{answer: string, sources: Array, images: Array}>}
     */
    async getScientificInfo(query) {
      return await this.search(query, "web", "searchResults", "International");
    }
    
    /**
     * Search trending topics with date filtering and sourcedAnswer output
     * @param {string} query - Search query
     * @param {string} fromDate - Start date in ISO format
     * @param {string} toDate - End date in ISO format
     * @param {string} region - Region filter ('Indonesia' or 'International')
     * @returns {Promise<{answer: string, sources: Array, images: Array}>}
     */
    async searchTrending(query, fromDate, toDate, region = "Indonesia") {
      const MAX_RETRIES = 2;
      const TIMEOUT_MS = 25000; // 25 seconds
      let lastError = null;

      async function makeRequest(attempt) {
        try {
          if (!LINKUP_API_KEY) {
            console.error('[Linkup] LINKUP_API_KEY not found or invalid');
            return {
              answer: "API configuration invalid. Contact administrator.",
              sources: [],
              images: []
            };
          }
          if (requestCount >= MAX_REQUESTS) {
            console.log('[Linkup] Max request limit reached');
            return {
              answer: "API usage limit reached. Try again later.",
              sources: [],
              images: []
            };
          }
          requestCount++;
          console.log(`[Linkup] Trending request #${requestCount} of ${MAX_REQUESTS}`);

          // Setup filters based on region
          let filters = {};
          if (region === "Indonesia") {
            filters = {
              domain_filter: {
                include: [".id", ".co.id", "kompas.com", "detik.com", "tribunnews.com", "liputan6.com", "okezone.com", "merdeka.com", "suara.com", "cnnindonesia.com", "tempo.co", "republika.co.id", "kumparan.com", "jabarprov.go.id", "jakarta.go.id", "kemendagri.go.id", "dpr.go.id", "bps.go.id"],
                exclude: []
              }
            };
            console.log("[Linkup] Using Indonesia source filter for trending");
          } else if (region === "International") {
            filters = {
              domain_filter: {
                exclude: [
                  ".id", ".co.id", "kompas.com", "detik.com", "tribunnews.com", "liputan6.com", "okezone.com", "merdeka.com", "suara.com", "cnnindonesia.com", "tempo.co", "republika.co.id", "kumparan.com", "sindonews.com", "jawapos.com", "suaramerdeka.com", "pikiran-rakyat.com", "bisnis.com", "beritasatu.com", "cnbcindonesia.com", "go.id", "jabarprov.go.id", "jakarta.go.id", "kemendagri.go.id", "dpr.go.id", "bps.go.id", "depkes.go.id", "kemdikbud.go.id"
                ],
                include: [
                  ".com", ".org", ".net", "bbc.com", "cnn.com", "nytimes.com", "theguardian.com", "reuters.com", "apnews.com", "washingtonpost.com", "economist.com", "forbes.com", "bloomberg.com"
                ]
              }
            };
            console.log("[Linkup] Using International source filter for trending");
          }

          // Format request for trending search with searchResults
          const requestOptions = {
            q: query,
            depth: "standard",
            outputType: "searchResults", // Use searchResults for trending
            includeImages: true,
            maxResults: 50,
            fromDate: fromDate,
            toDate: toDate,
            ...(Object.keys(filters).length > 0 && { filters })
          };

          // Use fetch with timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

          console.log(`[Linkup] Sending trending request to ${LINKUP_API_URL} (attempt ${attempt + 1})`);
          console.log(`[Linkup] Query: "${query}", Region: "${region}", Date: ${fromDate} to ${toDate}`);
          console.log(`[Linkup] Request options:`, JSON.stringify(requestOptions, null, 2));
          
          const response = await fetch(LINKUP_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${LINKUP_API_KEY}`
            },
            body: JSON.stringify(requestOptions),
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          console.log(`[Linkup] Trending response status: ${response.status}`);

          if (!response.ok) {
            const errorText = await response.text();
            console.error(`[Linkup] HTTP error response:`, errorText);
            throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
          }

          const responseText = await response.text();
          console.log(`[Linkup] Trending response text length: ${responseText.length}`);
          
          if (!responseText || responseText.trim() === '') {
            console.error('[Linkup] Empty response from trending API');
            return {
              answer: "Layanan pencarian trending sedang tidak tersedia. Silakan coba beberapa saat lagi.",
              sources: [],
              images: []
            };
          }

          let data;
          try {
            data = JSON.parse(responseText);
          } catch (parseError) {
            console.error('[Linkup] JSON parse error:', parseError);
            console.error('[Linkup] Raw response:', responseText);
            return {
              answer: "Format respons tidak valid dari layanan pencarian trending.",
              sources: [],
              images: []
            };
          }

          console.log('[Linkup] Parsed trending response data:', data);

          // Handle error responses
          if (data.error) {
            console.error('[Linkup] API returned error:', data.error);
            throw new Error(`API Error: ${data.error.message || data.error}`);
          }

          // Process and return the trending response
          return this.processResponse(data, query, region);

        } catch (error) {
          console.error(`[Linkup] Trending request attempt ${attempt + 1} failed:`, error);
          lastError = error;
          
          if (attempt < MAX_RETRIES - 1) {
            const delay = Math.pow(2, attempt) * 1000; // Exponential backoff
            console.log(`[Linkup] Retrying trending request in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
          
          throw error;
        }
      }

      // Try the request with retries
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          return await makeRequest.call(this, attempt);
        } catch (error) {
          if (attempt === MAX_RETRIES - 1) {
            // Last attempt failed
            console.error('[Linkup] All trending retry attempts failed:', lastError);
            return {
              answer: "Maaf, layanan pencarian trending sedang mengalami gangguan. Silakan coba lagi nanti.",
              sources: [],
              images: []
            };
          }
        }
      }
    }
   
   /**
    * Extract facts from answer text
    * @param {string} answer - Answer text
    * @returns {Array} Array of extracted facts
    */
   extractFactsFromAnswer(answer) {
     if (!answer || typeof answer !== 'string') {
       return [];
     }
     
     // Simple fact extraction - split by sentences and filter meaningful ones
     const sentences = answer.split(/[.!?]+/).filter(s => s.trim().length > 20);
     return sentences.map(s => s.trim()).filter(s => s.length > 0);
   }
   
   /**
    * Verify article content against sources
    * @param {string} content - Article content
    * @param {Array} sources - Sources array
    * @returns {Object} Verification result
    */
   verifyArticleContent(content, sources) {
     if (!content || !sources || sources.length === 0) {
       return {
         verified: false,
         confidence: 0,
         matchingSources: []
       };
     }
     
     const contentWords = content.toLowerCase().split(/\s+/);
     const matchingSources = [];
     
     sources.forEach(source => {
       if (source.snippet) {
         const snippetWords = source.snippet.toLowerCase().split(/\s+/);
         const commonWords = contentWords.filter(word => 
           word.length > 3 && snippetWords.includes(word)
         );
         
         if (commonWords.length > 2) {
           matchingSources.push({
             ...source,
             matchCount: commonWords.length,
             commonWords: commonWords.slice(0, 5) // First 5 common words
           });
         }
       }
     });
     
     const confidence = Math.min(matchingSources.length / sources.length, 1);
     
     return {
       verified: confidence > 0.3,
       confidence: Math.round(confidence * 100),
       matchingSources: matchingSources
     };
   }
   
   /**
    * Format sources for display
    * @param {Array} sources - Sources array
    * @returns {Array} Formatted sources
    */
   formatSources(sources) {
     if (!sources || !Array.isArray(sources)) {
       return [];
     }
     
     return sources.map((source, index) => ({
       id: index + 1,
       title: source.title || 'Untitled Source',
       snippet: source.snippet || 'No description available',
       url: source.url || '#',
       domain: source.url ? new URL(source.url).hostname : 'Unknown',
       isFallback: source.isFallback || false
     }));
   }
   
   /**
    * Format images for display
    * @param {Array} images - Images array
    * @returns {Array} Formatted images
    */
   formatImages(images) {
     if (!images || !Array.isArray(images)) {
       return [];
     }
     
     return images.map((image, index) => ({
       id: index + 1,
       url: image.url || image.src || '',
       title: image.title || image.alt || `Image ${index + 1}`,
       description: image.description || image.caption || '',
       source: image.source || 'Unknown'
     })).filter(img => img.url); // Only include images with valid URLs
   }
    
    /**
     * Clear the search results cache
     * @public
     */
    clearCache() {
      this.cache.clear()
      if (process.env.NODE_ENV === 'development') {
        console.log('🗑️ Search cache cleared')
      }
    }
    
    /**
     * Get cache statistics
     * @returns {Object} Cache size and timeout information
     * @public
     */
    getCacheStats() {
      return {
        size: this.cache.size,
        timeout: this.cacheTimeout,
        maxSize: 100
      }
    }
}

// Export functions for backward compatibility
export async function searchWithLinkup(query, searchType = "web", outputType = "searchResults", region = "Indonesia") {
  const service = new LinkupService();
  return await service.search(query, searchType, outputType, region);
}

export async function getFactualInfo(query) {
  const service = new LinkupService();
  return await service.getFactualInfo(query);
}

export async function getScientificInfo(query) {
  const service = new LinkupService();
  return await service.getScientificInfo(query);
}

export function resetRequestCount() {
  const service = new LinkupService();
  return service.resetRequestCount();
}

export function extractFactsFromAnswer(answer) {
  const service = new LinkupService();
  return service.extractFactsFromAnswer(answer);
}

export function verifyArticleContent(content, sources) {
  const service = new LinkupService();
  return service.verifyArticleContent(content, sources);
}

export function formatSources(sources) {
  const service = new LinkupService();
  return service.formatSources(sources);
}

export function formatImages(images) {
  const service = new LinkupService();
  return service.formatImages(images);
}

// Create and export default instance
const linkupService = new LinkupService();
export default linkupService;

// Also export the class for advanced usage
export { LinkupService };